import {
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  Input,
  afterNextRender,
  inject,
  input,
  output,
  computed,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { NotifyService } from "../../notify/notify.service";
import { ViewportService } from "../../shared/viewport/viewport.service";
import { tableColumn } from "./table-column.const";
import { quoteSource } from "../../quote/quote-source.const";
import { ITableColumn } from "./table-column.interface";
import { ITableRow } from "./table-row.interface";
import { MenuItem } from "primeng/api";
import { ButtonDirective } from "primeng/button";
import { rateDiscriminator } from "../../rate/rate-discriminator.const";
import { EmptyMessageComponent } from "../empty-message/empty-message.component";
import { Popover } from "primeng/popover";
import { PopoverArrowDirective } from "app/ui/popover/popover-arrow.directive";
import { TooltipOnEllipsisDirective } from "../tooltip/tooltip-on-ellipsis.directive";
import { Bind } from "primeng/bind";
import { Table, TableModule } from "primeng/table";
import { ProgressSpinner } from "primeng/progressspinner";
import { Skeleton } from "primeng/skeleton";
import { CarrierSelectComponent } from "../dropdown/carrier-select/carrier-select.component";
import { CarrierMultiselectComponent } from "../dropdown/carrier-multiselect/carrier-multiselect.component";
import { RateMultiselectComponent } from "../dropdown/rate-multiselect/rate-multiselect.component";
import { Tooltip } from "primeng/tooltip";
import { SurchargeTypeSelectComponent } from "../dropdown/surcharge-type-select/surcharge-type-select.component";
import { SurchargeTypeAutocompleteComponent } from "../dropdown/surcharge-type-autocomplete/surcharge-type-autocomplete.component";
import { LocationIconComponent } from "../location-icon/location-icon.component";
import { LocationSelectComponent } from "../dropdown/location-select/location-select.component";
import { CountrySelectComponent } from "../dropdown/country-select/country-select.component";
import { CountryMultiselectComponent } from "../dropdown/country-multiselect/country-multiselect.component";
import { LoadTypeSelectComponent } from "../dropdown/load-type-select/load-type-select.component";
import { TransportTypeSelectComponent } from "../dropdown/transport-type-select/transport-type-select.component";
import { UnitTypeSelectComponent } from "../dropdown/unit-type-select/unit-type-select.component";
import { MarginTypeSelectComponent } from "../dropdown/margin-type-select/margin-type-select.component";
import { MultiSelect } from "primeng/multiselect";
import { FormsModule } from "@angular/forms";
import { Select } from "primeng/select";
import { ClipboardRowComponent } from "./clipboard-row/clipboard-row.component";
import { OnlineCarrierButtonComponent } from "./online-carrier-button/online-carrier-button.component";
import { OnlineCarrierInfoComponent } from "./online-carrier-info/online-carrier-info.component";
import { OnlineCarrierAction } from "../../rate/online-carrier.interface";
import { onlineCarrierStatus } from "../../rate/online-carrier-status.const";
import { WysiwygComponent } from "../wysiwyg/wysiwyg.component";
import { QuoteListRowComponent } from "./quote-list-row/quote-list-row.component";
import { Quote } from "../../quote/quote.type";
import { DatePicker } from "primeng/datepicker";
import { DirectionSelectComponent } from "../dropdown/direction-select/direction-select.component";
import { CurrencySelectComponent } from "../dropdown/currency-select/currency-select.component";
import { InputText } from "primeng/inputtext";
import { PriceBoxComponent } from "./price-box/price-box.component";
import { PercentBoxComponent } from "./percent-box/percent-box.component";
import { ContextMenu } from "primeng/contextmenu";
import { DatePipe } from "@angular/common";
import { CountryNamePipe } from "../../shared/countries/country-name.pipe";
import { SafeHtmlPipe } from "../../shared/html/safe-html.pipe";
import { CurrencyPipe } from "../../shared/currencies/currency.pipe";
import { TimeAgoPipe } from "../../shared/time-ago/time-ago.pipe";

@Component({
  selector: "app-table",
  templateUrl: "./table.component.html",
  styleUrls: ["./table.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonDirective,
    EmptyMessageComponent,
    Bind,
    TableModule,
    ProgressSpinner,
    Skeleton,
    CarrierSelectComponent,
    CarrierMultiselectComponent,
    RateMultiselectComponent,
    Tooltip,
    SurchargeTypeSelectComponent,
    SurchargeTypeAutocompleteComponent,
    LocationIconComponent,
    LocationSelectComponent,
    CountrySelectComponent,
    CountryMultiselectComponent,
    LoadTypeSelectComponent,
    TransportTypeSelectComponent,
    UnitTypeSelectComponent,
    MarginTypeSelectComponent,
    MultiSelect,
    FormsModule,
    Select,
    ClipboardRowComponent,
    OnlineCarrierButtonComponent,
    OnlineCarrierInfoComponent,
    Popover,
    PopoverArrowDirective,
    TooltipOnEllipsisDirective,
    WysiwygComponent,
    QuoteListRowComponent,
    DatePicker,
    DirectionSelectComponent,
    CurrencySelectComponent,
    InputText,
    PriceBoxComponent,
    PercentBoxComponent,
    ContextMenu,
    DatePipe,
    SafeHtmlPipe,
    CurrencyPipe,
    CountryNamePipe,
    TimeAgoPipe,
  ],
  host: {
    "[class.table--editing]": "editingIndex !== null",
    "[class.table--scrollable]": "isScrollable()",
    "[class.table--record-counter]": "showRecordCounter()",
  },
})
export class TableComponent {
  notifyService = inject(NotifyService);

  @Input() public addable = false;
  @Input() public deletable = false;
  @Input() public duplicatable = false;
  public readonly selectable = input(false);
  public readonly editable = input(false);
  public readonly reorderable = input(false);
  public readonly onlineCarriers = input<OnlineCarrierAction[]>([]);
  // Carrier types left to their own button by Search all.
  public readonly searchAllSkips = input<readonly string[]>([]);
  // The organization's balance; null until known, which leaves paid carriers usable.
  public readonly searchCredits = input<number | null>(null);
  public readonly headerSelectable = input(true);
  public readonly rowsPerPageOptions = input([25, 50, 100, 200]);
  public readonly dataKey = input("id");

  public readonly columns = input<ITableColumn[]>(undefined);
  public readonly value = input<ITableRow[]>(undefined);
  public readonly newValue = input<ITableRow>(undefined);
  public readonly loading = input<boolean>(undefined);
  // Rows are still arriving for a table that already has some: placeholders go
  // below the loaded rows instead of replacing them, so what is there stays
  // readable. `loading` wins when both are set - there is nothing to append to.
  public readonly loadingMoreRows = input(false);
  // How many placeholders `loading` shows; a table reloading rows it already
  // shows passes their count, so the skeleton does not resize the page.
  public readonly skeletonRowCount = input<number | null>(null);
  public readonly emptyMessage = input("No elements found");
  public readonly addLabel = input("Add New");
  public readonly addPosition = input<"top" | "bottom">("bottom");
  @Input() public rowClass: (rowData) => string;
  @Input() public rowDisabled: (columnName: string, rowData) => boolean;
  @Input() public rowSelectable: (rowData) => boolean;
  public readonly rowBusy = input<(rowData) => boolean>(undefined);

  @Input() public selection: ITableRow[];

  @Input() public first = 0;
  public readonly paginator = input<boolean>(undefined);
  // Keeps the paginator bar, with a record/selection count, on screen even when
  // the list fits one page. Single-row editors that still bind totalRecords
  // opt out.
  public readonly recordCounter = input(true);
  @Input() public rows: number;
  @Input() public totalRecords: number;

  public readonly contextMenuItems = input<MenuItem[]>(undefined);
  public readonly contextMenuSelection = input<ITableRow>(undefined);
  public readonly contextMenuSelectionChange = output<any>();

  public readonly changePage = output<any>();
  public readonly sortRows = output<any>();
  public readonly rowClick = output<{
    model: ITableRow;
    originalEvent: Event;
  }>();
  public readonly unSelectRow = output<ITableRow>();
  public readonly selectRow = output<ITableRow>();
  public readonly saveRow = output<any>();
  public readonly loadOnlineCarrier = output<string>();
  // Search all left out carriers the search allowance no longer covers.
  public readonly searchAllowanceReached = output<void>();
  public readonly deleteRow = output<ITableRow>();
  public readonly createRow = output<any>();
  public readonly selectionChange = output<ITableRow[]>();
  public readonly reorderRows = output<any>();

  public editingIndex: number = null;
  public editingRowData: any;
  public tableColumn = tableColumn;
  public quoteSource = quoteSource;

  // Drives the elevation of the pinned actions column: it is only meaningful
  // while there is content scrolled behind it.
  public readonly isScrollable = signal(false);

  // Below md the columns flagged `hideOnMobile` are dropped from the header,
  // the skeleton and the rows alike, so the table fits the card without a
  // horizontal scrollbar.
  private readonly viewportService = inject(ViewportService);
  private readonly isMobile = this.viewportService.isMobile;
  public readonly visibleColumns = computed(() =>
    this.isMobile()
      ? (this.columns() ?? []).filter((column) => !column.hideOnMobile)
      : (this.columns() ?? []),
  );

  private readonly elementRef = inject(ElementRef);
  private readonly injector = inject(Injector);
  private readonly dataTable = viewChild(Table);

  constructor() {
    const destroyRef = inject(DestroyRef);

    afterNextRender(() => {
      const container: HTMLElement = (
        this.elementRef.nativeElement as HTMLElement
      ).querySelector(".p-datatable-table-container");

      if (!container) {
        return;
      }

      // Watch the table too — adding columns or opening an editor changes the
      // content width without resizing the container.
      const table = container.querySelector("table");
      const unobserve = this.viewportService.observeResize(
        table ? [container, table] : [container],
        () =>
          this.isScrollable.set(container.scrollWidth > container.clientWidth),
      );

      destroyRef.onDestroy(unobserve);
    });
  }

  public static readonly SKELETON_ROW_COUNT = 10;
  // Enough to read as "more is coming" under the loaded rows without pushing
  // them off screen. The incoming count is unknown, so any number is a hint.
  public static readonly APPENDED_SKELETON_ROW_COUNT = 3;
  private static readonly SKELETON_ROW_FLAG = "isSkeletonRow";
  private static readonly NARROW_COLUMN_TYPES: ReadonlySet<string> = new Set([
    tableColumn.boolean,
    tableColumn.opened,
    tableColumn.editable,
    tableColumn.download,
    tableColumn.copy,
    tableColumn.comment,
    tableColumn.editableComment,
    tableColumn.warning,
    tableColumn.info,
    tableColumn.debug,
    tableColumn.link,
  ]);

  public readonly skeletonRows = computed<ITableRow[]>(() => {
    if (this.loading()) {
      return TableComponent.buildSkeletonRows(
        this.skeletonRowCount() ?? TableComponent.SKELETON_ROW_COUNT,
      );
    }

    if (!this.loadingMoreRows()) {
      return this.value();
    }

    const rows = this.value() ?? [];

    return [
      ...rows,
      ...TableComponent.buildSkeletonRows(
        TableComponent.APPENDED_SKELETON_ROW_COUNT,
        rows.length,
      ),
    ];
  });

  // Carries the row key under both names the app uses as `dataKey`, so an
  // appended placeholder never collides with a loaded row in p-table's
  // selection bookkeeping.
  private static buildSkeletonRows(count: number, offset = 0): ITableRow[] {
    return Array.from({ length: count }, (_, i) => {
      const key = `skeleton-${offset + i}`;

      return {
        id: key,
        rowKey: key,
        [TableComponent.SKELETON_ROW_FLAG]: true,
      } as unknown as ITableRow;
    });
  }

  public isSkeletonRow(rowData): boolean {
    return !!rowData?.[TableComponent.SKELETON_ROW_FLAG];
  }

  public isNarrowColumn(type: string): boolean {
    return TableComponent.NARROW_COLUMN_TYPES.has(type);
  }

  public isCellDisabled(column: ITableColumn, rowData): boolean {
    return this.rowDisabled ? this.rowDisabled(column.field, rowData) : false;
  }

  // A disabled editor cannot be filled in, so marking it required is noise.
  // Takes the already-resolved flag: the template computes it once per cell and
  // shares it with the editor's own [disabled] binding.
  public showRequiredMarker(
    column: ITableColumn,
    cellDisabled: boolean,
  ): boolean {
    return !!column.required && !cellDisabled;
  }

  public hasDetailsTooltip(quote: Quote | null): boolean {
    return !!quote && !QuoteListRowComponent.rendersOwnTooltip(quote);
  }

  public isCellInvalid(column: ITableColumn, rowData): boolean {
    return column.invalid ? column.invalid(rowData) : false;
  }

  public overlayContent: string = null;

  private editingIndexBackup: number = null;

  public getColumnCount(): number {
    return (
      this.visibleColumns().length +
      Number(this.selectable()) +
      Number(this.reorderable()) +
      Number(this.duplicatable) +
      Number(this.deletable || this.editable())
    );
  }

  // TODO: Can be moved to separate Pipe
  public isToday(date: Date): boolean {
    return new Date().toDateString() === date.toDateString();
  }

  public getDateRangeOptions(colData) {
    return {
      startDate: colData[0],
      endDate: colData[1],
    };
  }

  public isPriceBoxNullable(rowData): boolean {
    return rowData.getDiscriminator() === rateDiscriminator.fcl;
  }

  // Methods, not computed(): selection, first, rows and totalRecords are plain
  // mutable inputs. An unbound totalRecords turns into NaN after an add/delete
  // (`undefined += 1`), hence the finiteness check rather than `!= null`.
  public showRecordCounter(): boolean {
    return this.recordCounter() && Number.isFinite(this.totalRecords);
  }

  public recordCounterLabel(): string {
    const total = this.totalRecords;
    const selected = this.selection?.length ?? 0;

    if (selected > 0) {
      return `${selected} selected out of ${total}`;
    }

    if (!this.rows || total <= this.rows) {
      return `${total} ${total === 1 ? "record" : "records"}`;
    }

    const last = Math.min(this.first + this.rows, total);

    return `${this.first + 1}–${last} of ${total} records`;
  }

  public onPageChange(event): void {
    this.first = event.first;
    this.changePage.emit(event);
  }

  public onAddRow(): void {
    this.closeEditor();
    const rowData = this.newValue().getClone();
    const rowIndex = this.value().length + this.first;
    this.value().push(rowData);
    this.editingRowData = rowData;
    this.rows += 1;
    this.totalRecords += 1;
    this.openEditor(rowIndex);
    this.scrollEditorIntoView();
  }

  // Connected only: a renewable click starts a renewal, not a search.
  public readonly searchAllCarriers = computed(() =>
    this.onlineCarriers().filter(
      (carrier) =>
        !this.searchAllSkips().includes(carrier.type) &&
        carrier.status === onlineCarrierStatus.connected &&
        !carrier.loading &&
        !carrier.disabled,
    ),
  );
  public readonly searchableCarriers = computed(() =>
    this.withinSearchCredits(this.searchAllCarriers()),
  );

  public onLoadOnlineCarrier(type: string): void {
    this.closeEditor();
    this.loadOnlineCarrier.emit(type);
  }

  public onSearchAll(): void {
    this.closeEditor();
    this.searchableCarriers().forEach((carrier) =>
      this.loadOnlineCarrier.emit(carrier.type),
    );
    if (this.searchableCarriers().length < this.searchAllCarriers().length) {
      this.searchAllowanceReached.emit();
    }
  }

  // Paid carriers in button order, as many as the balance pays for.
  private withinSearchCredits(
    carriers: OnlineCarrierAction[],
  ): OnlineCarrierAction[] {
    let creditsLeft = this.searchCredits() ?? Infinity;

    return carriers.filter((carrier) => !carrier.paid || creditsLeft-- > 0);
  }

  public onSelectionChange(selection): void {
    this.selection = selection;
    this.selectionChange.emit(this.selection);
  }

  public onSelectRow(event): void {
    event.originalEvent.stopPropagation();
    // Keyboard range selection (Ctrl+A, Ctrl+Shift+Home/End, Shift+Space)
    // emits an array of rows; selectRow carries a single row.
    if (Array.isArray(event.data)) {
      return;
    }
    this.selectRow.emit(event.data);
  }

  public onUnselectRow(event): void {
    event.originalEvent.stopPropagation();
    this.unSelectRow.emit(event.data);
    this.selectionChange.emit(this.selection);
  }

  // The whole selection cell toggles the row, as the checkbox does, instead
  // of letting the click reach onClickRow and open the record.
  public onClickSelectionCell(
    rowData: ITableRow,
    rowIndex: number,
    event: MouseEvent,
  ): void {
    event.stopPropagation();

    if (
      (event.target as HTMLElement).closest("p-tablecheckbox") ||
      rowData.isBusy ||
      (this.rowSelectable && !this.rowSelectable(rowData)) ||
      this.editingIndex === rowIndex
    ) {
      return;
    }

    this.dataTable().toggleRowWithCheckbox(
      { originalEvent: event, rowIndex },
      rowData,
    );
  }

  public onClickRow(rowIndex, originalEvent): void {
    if (
      originalEvent.target &&
      (originalEvent.target as HTMLElement).className === "p-checkbox-input"
    ) {
      return;
    }
    const rowData = this.getRowDataForIndex(rowIndex);
    if (this.rowSelectable && !this.rowSelectable(rowData)) {
      return;
    }
    if (rowData.isBusy) {
      return;
    }
    if (this.editable() && rowIndex !== this.editingIndex) {
      const isSelected = this.isRowSelected(rowIndex);

      // Select row
      const selectable = this.selectable();
      if (selectable && !isSelected) {
        this.selection = this.selection
          ? [...this.selection, rowData]
          : [rowData];
        this.selectRow.emit(rowData);
        this.selectionChange.emit(this.selection);
      }

      // Open editor
      if (!selectable || (selectable && isSelected)) {
        this.closeEditor();
        this.openEditor(rowIndex);
        this.editingRowData = this.getRowDataForIndex(rowIndex).getClone();
      }
    }
    this.rowClick.emit({
      model: this.getRowDataForIndex(rowIndex),
      originalEvent: event,
    });
  }

  public onSaveRow(event: MouseEvent): void {
    event.stopPropagation();
    const previousRowData = this.getRowDataForIndex(this.editingIndex);
    this.setRowDataForIndex(this.editingIndex, this.editingRowData);

    // The edited row is replaced by a clone, so the selection must follow it
    const selectionIndex = this.selection
      ? this.selection.indexOf(previousRowData)
      : -1;
    if (selectionIndex >= 0) {
      this.selection = [
        ...this.selection.slice(0, selectionIndex),
        this.editingRowData,
        ...this.selection.slice(selectionIndex + 1),
      ];
      this.selectionChange.emit(this.selection);
    }

    this.saveRow.emit(this.editingRowData);
    this.closeEditor(false);
  }

  public isNewRow(rowData: ITableRow): boolean {
    return !rowData.getId();
  }

  public onCancelRow(event: MouseEvent): void {
    event.stopPropagation();
    this.closeEditor();
  }

  public onEditRow(editedColumn): void {
    if (editedColumn.change) {
      editedColumn.change(this.editingRowData);
    }
  }

  public onDeleteRow(event: MouseEvent, rowIndex): void {
    event.stopPropagation();
    this.notifyService
      .confirm("Are you sure you want to delete this element?", "Delete", {
        destructive: true,
      })
      .subscribe(() => {
        const realRowIndex = rowIndex - this.first;
        const rowData = this.value().splice(realRowIndex, 1);
        this.deleteRow.emit(rowData[0]);
        this.closeEditor();
      });
  }

  public onDuplicateRow(event: MouseEvent, rowIndex): void {
    event.stopPropagation();
    const newRowIndex = this.value().length + this.first;
    const newRowData: ITableRow = this.getRowDataForIndex(rowIndex).getClone();
    newRowData.setId(null);
    this.value().push(newRowData);
    this.editingRowData = newRowData;
    this.openEditor(newRowIndex);
    this.scrollEditorIntoView();
  }

  public onReorder(event): void {
    this.reorderRows.emit(event);
  }

  public isRowSelected(rowIndex): boolean {
    if (!this.selection) {
      return false;
    }

    return this.selection.indexOf(this.getRowDataForIndex(rowIndex)) >= 0;
  }

  public openOverlay(event, text, overlayPanel: Popover) {
    event.stopPropagation();
    this.overlayContent = text;

    if (!overlayPanel.overlayVisible()) {
      overlayPanel.show(event);
      return;
    }

    // Popover clears its internal `render` flag right after emitting `onHide`,
    // so re-showing from inside the handler is discarded — defer it by a tick.
    // `currentTarget` is nulled once dispatch ends, so capture it now.
    const target = event.currentTarget as HTMLElement;
    const subscription = overlayPanel.onHide.subscribe(() => {
      subscription.unsubscribe();
      setTimeout(() => overlayPanel.show(null, target));
    });
    overlayPanel.hide();
  }

  public handleSaveError(): void {
    this.editingIndex = this.editingIndexBackup;
  }

  private getRowDataForIndex(rowIndex) {
    return this.value()[rowIndex - this.first];
  }

  private setRowDataForIndex(rowIndex, rowData) {
    this.value()[rowIndex - this.first] = rowData;
  }

  private closeEditor(removeEmptyRow = true): void {
    // Removes previously edited row
    if (
      removeEmptyRow &&
      this.editingIndex !== null &&
      this.getRowDataForIndex(this.editingIndex) &&
      !this.getRowDataForIndex(this.editingIndex).getId()
    ) {
      this.value().splice(this.value().length - 1, 1);
      this.rows -= 1;
      this.totalRecords -= 1;
    }

    // Closes the editor
    this.editingIndexBackup = this.editingIndex;
    this.editingIndex = null;
  }

  private openEditor(index: number): void {
    this.editingIndex = index;
  }

  // A new row is appended at the bottom of the page, which can be far from the
  // "add" button. The editor row exists only after the next render.
  private scrollEditorIntoView(): void {
    afterNextRender(
      () => {
        (this.elementRef.nativeElement as HTMLElement)
          .querySelector("tr.p-editor-enabled")
          ?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      },
      { injector: this.injector },
    );
  }
}
